/**
 * floating-atc.js
 *
 * Sticky "Add to Cart" bar that appears from below when the main buybox
 * button scrolls out of view.
 *
 * Responsibilities:
 * 1. VISIBILITY — IntersectionObserver watches the buybox submit button.
 *    When it leaves the viewport → slide the bar up.
 *    When it re-enters → slide the bar back down.
 *
 * 2. STATE SYNC — Mirrors the current purchase mode (subscribe / otp) from
 *    the main form so button text, price, and the "Save XX%" badge stay in
 *    sync without duplicating any logic from product.js.
 *
 * 3. SUBMIT — Clicking the floating button calls form.requestSubmit(), which
 *    triggers the existing submit handler in product.js. All cart logic,
 *    loading states on the main button, and error handling are inherited.
 *    A MutationObserver mirrors the main button's disabled state so the
 *    floating button is visually locked while the cart operation runs.
 */

(function () {
  'use strict';

  var floatingAtc = document.querySelector('.js-floating-atc');
  if (!floatingAtc) return;

  var form = document.querySelector('.js-product-form');
  if (!form) return;

  var mainBtn = form.querySelector('.js-product-submit');
  if (!mainBtn) return;

  // ── Floating ATC DOM refs ────────────────────────────────────────────────
  var floatingBtn   = floatingAtc.querySelector('.js-floating-atc-btn');
  var floatingText  = floatingAtc.querySelector('.js-floating-atc-text');
  var floatingPrice = floatingAtc.querySelector('.js-floating-atc-price');
  var floatingSave  = floatingAtc.querySelector('.js-floating-atc-save');

  // ── Data from the main form (same data-attributes used by product.js) ────
  var subPrice        = form.dataset.subPrice;
  var otpPrice        = form.dataset.otpPrice;
  var btnSubscribe    = form.dataset.btnSubscribe;
  var btnOtp          = form.dataset.btnOtp;
  var btnSubLoading   = form.dataset.btnSubscribeLoading;
  var btnOtpLoading   = form.dataset.btnOtpLoading;
  var discountPct     = parseInt(floatingAtc.dataset.discountPct, 10) || 0;
  var hasSubscription = !form.dataset.noSubscription;

  // ── Helpers ──────────────────────────────────────────────────────────────
  function getCurrentMode() {
    var checked = form.querySelector('input[name="purchase_mode"]:checked');
    return checked ? checked.value : (hasSubscription ? 'subscribe' : 'otp');
  }

  // Updates the floating button text / price / save badge to match the
  // current purchase mode. Called on init and on every mode change.
  function syncState() {
    var mode = getCurrentMode();
    var isSubscribe = mode === 'subscribe';

    if (floatingText)  floatingText.textContent  = isSubscribe ? btnSubscribe : btnOtp;
    if (floatingPrice) floatingPrice.textContent = isSubscribe ? subPrice : otpPrice;

    if (floatingSave) {
      // Show "Save XX%" only when subscribe is active AND there is a discount.
      floatingSave.classList.toggle(
        'floating-atc__save--visible',
        isSubscribe && discountPct > 0
      );
    }

    if (floatingBtn) {
      floatingBtn.setAttribute('aria-label', isSubscribe ? btnSubscribe : btnOtp);
    }
  }

  // ── Initial state ────────────────────────────────────────────────────────
  // product.js runs first (both scripts are defer, order is preserved) and
  // has already called setMode(), so the radios reflect the correct state.
  syncState();

  // ── Listen for purchase mode changes ─────────────────────────────────────
  form.addEventListener('change', function (event) {
    if (event.target.name === 'purchase_mode') {
      syncState();
    }
  });

  // ── Show / hide via IntersectionObserver ─────────────────────────────────
  // Observing the main submit button: when it is NOT intersecting
  // (i.e., scrolled out of view) we reveal the floating bar.
  new IntersectionObserver(function (entries) {
    var buyboxVisible = entries[0].isIntersecting;
    floatingAtc.classList.toggle('floating-atc--visible', !buyboxVisible);
    floatingAtc.setAttribute('aria-hidden', buyboxVisible ? 'true' : 'false');
  }, { threshold: 0 }).observe(mainBtn);

  // ── Floating button click → submit the main form ─────────────────────────
  if (floatingBtn) {
    floatingBtn.addEventListener('click', function () {
      var mode = getCurrentMode();

      // Show a loading state on the floating button while the request runs.
      floatingBtn.disabled = true;
      if (floatingText) {
        floatingText.textContent = mode === 'subscribe' ? btnSubLoading : btnOtpLoading;
      }

      // Delegate to the existing submit handler in product.js.
      // requestSubmit() fires the 'submit' event (which product.js intercepts),
      // unlike form.submit() which bypasses JS listeners.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        // Fallback for iOS < 15.4 / Safari < 15.4
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    });
  }

  // ── Restore state on bfcache restoration ─────────────────────────────────
  // The browser freezes the DOM mid-submit, so the floating button can be
  // left disabled with loading text. pageshow with persisted:true fires when
  // the user navigates back; we re-enable and re-sync synchronously so the
  // bar is instantly usable without waiting for the MutationObserver cascade.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    if (floatingBtn) floatingBtn.disabled = false;
    syncState();
  });

  // ── Mirror main button disabled state ────────────────────────────────────
  // product.js re-enables mainBtn on error recovery; we mirror that here
  // so the floating button unlocks and restores its text too.
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.attributeName !== 'disabled') return;
      if (!floatingBtn) return;

      floatingBtn.disabled = mainBtn.disabled;

      // If the main button was re-enabled (error path), restore floating text.
      if (!mainBtn.disabled) {
        syncState();
      }
    });
  }).observe(mainBtn, { attributes: true });
}());
